import { Fragment, useContext, useEffect, useReducer, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Ban, Loader2, Pencil } from 'lucide-react';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { Skeleton } from 'components/ui/skeleton';
import { useToast } from 'components/ui/toast';
import { ConfirmDeleteDialog } from 'components/ConfirmDeleteDialog';
import AppContext from 'contexts/AppContext';
import { hasUserPermission, PERMISSIONS } from 'utils/permissionUtils';
import { hasUnlocatedAddress } from 'utils/unlocatedAddress';
import UnlocatedBadge from 'components/UnlocatedBadge';
import { apiErrorMessage, saveErrorMessage } from 'utils/errorMessage';
import { usePageCrumb } from 'components/admin/AppTopbar';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from 'i18n';
import { useTechnician, useCreateTechnician, useUpdateTechnician } from 'hooks/useTechnicians';
import { ADDRESS_KINDS, type AddressInput, type AddressKind, type Technician } from 'api/techniciansApi';
import type { AdminAuditEvent } from 'api/adminAuditApi';
import { provisioningNotice } from 'utils/jiraProvisioning';
import { formatDate } from 'utils/formatDate';
import { ApiError } from 'utils/unwrapResponse';
import { TECHNICIAN_DEFAULT_RIGHTS, type UserRights } from 'api/usersApi';
import {
  addressErrors,
  controlId,
  FIELD_ORDER,
  formErrors,
  isFilledAddress,
  exclusionReasonLabel,
  nonOverlappingSecondary,
  parseArea,
  parseTeamSize,
  type AddressDraft,
  type FormErrors,
  type LogisticsForm,
} from './validation';
import ReferentialPageHeader from '../AdminReferentials/components/ReferentialPageHeader';
import GeneralSection, { type GeneralValues } from './GeneralSection';
import ScopeSection from './ScopeSection';
import SkillsSection from './SkillsSection';
import ActivitySection from './ActivitySection';
import RatingsSection from './RatingsSection';
import UnavailabilitySection from './UnavailabilitySection';
import RightsSection from './RightsSection';
import LogisticsSection from './LogisticsSection';
import OpsNoteSection from './OpsNoteSection';
import ExclusionDialog, { type ExclusionDialogMode } from './ExclusionDialog';
import HistoryDrawer, { HISTORY_BUTTON_ID, lastModifiedText } from './HistoryDrawer';

interface FormState extends GeneralValues, LogisticsForm {
  isActive: boolean;
  countryCode: string;
  geoAreaIds: number[];
  secondaryGeoAreaIds: number[];
  specialtyIds: number[];
  expertiseIds: number[];
}

type Action = { type: 'load'; technician: Technician } | { type: 'patch'; patch: Partial<FormState> };

/**
 * The identifier the person signs in with, derived: given name as typed, family name in capitals
 * (the French administrative convention). Capped at the 120 the `name` schema allows, so a long
 * pair degrades instead of coming back as a 400.
 */
const derivedUsername = (firstName: string, lastName: string): string =>
  [firstName.trim(), lastName.trim().toUpperCase()].filter(Boolean).join(' ').slice(0, 120);

// The language the country proposes. Only a proposal: a manual pick is never overwritten.
const COUNTRY_LANG: Record<string, string> = { FR: 'fr', ES: 'es', GB: 'en' };

// Bannir and Modifier le motif never coexist: the focus lands on whichever the record now shows.
const EXCLUSION_ACTION_ID = 'technician-exclusion-action';

// The header's action pair is outside the <form>, and `<button form="…">` is what reconnects it.
const FORM_ID = 'technician-record-form';

// A record written before the enum, or with a regional tag, must still show a language.
const resolveLang = (lang: string): string =>
  (SUPPORTED_LOCALES as readonly string[]).includes(lang) ? lang : DEFAULT_LOCALE;

const EMPTY_ADDRESS: AddressDraft = { line1: '', postalCode: '', city: '', areaSqm: '', placeId: null };

const draftsOf = (technician: Technician): Record<AddressKind, AddressDraft> => {
  const drafts = { billing: EMPTY_ADDRESS, delivery: EMPTY_ADDRESS, warehouse: EMPTY_ADDRESS };
  for (const address of technician.addresses) {
    drafts[address.kind] = {
      line1: address.line1 ?? '',
      postalCode: address.postalCode ?? '',
      city: address.city ?? '',
      areaSqm: address.areaSqm === null ? '' : String(address.areaSqm),
      placeId: address.placeId,
    };
  }
  return drafts;
};

// The full set, filled kinds only: a kind left out is deleted server-side (AC18).
const addressesPayload = ({ addresses, referenceKind }: LogisticsForm): AddressInput[] =>
  ADDRESS_KINDS.filter((kind) => isFilledAddress(addresses[kind])).map((kind) => ({
    kind,
    line1: addresses[kind].line1.trim() || null,
    postalCode: addresses[kind].postalCode.trim() || null,
    city: addresses[kind].city.trim() || null,
    areaSqm: kind === 'billing' ? null : parseArea(addresses[kind].areaSqm),
    isDefault: kind === referenceKind,
    placeId: addresses[kind].placeId,
  }));

const EMPTY: FormState = {
  username: '',
  email: '',
  firstName: '',
  lastName: '',
  company: '',
  phone: '',
  password: '',
  lang: DEFAULT_LOCALE,
  contractType: '',
  profileKind: '',
  experienceLevel: '',
  vehicleType: '',
  teamSize: '',
  isActive: true, // creation default, and the value the design shows on the create form
  countryCode: '',
  geoAreaIds: [],
  secondaryGeoAreaIds: [],
  specialtyIds: [],
  expertiseIds: [],
  addresses: { billing: EMPTY_ADDRESS, delivery: EMPTY_ADDRESS, warehouse: EMPTY_ADDRESS },
  referenceKind: null,
};

// One reducer for the whole record: the sections patch it, nothing owns a slice of its own, so
// the country change that clears the areas is a single atomic update rather than two renders.
const reducer = (state: FormState, action: Action): FormState => {
  switch (action.type) {
    case 'load':
      return {
        ...EMPTY,
        email: action.technician.email,
        firstName: action.technician.profile.firstName ?? '',
        lastName: action.technician.profile.lastName ?? '',
        company: action.technician.profile.company ?? '',
        phone: action.technician.profile.phone ?? '',
        // `??`: a record served before the F9 columns carries none of the five.
        contractType: action.technician.profile.contractType ?? '',
        profileKind: action.technician.profile.profileKind ?? '',
        experienceLevel: action.technician.profile.experienceLevel ?? '',
        vehicleType: action.technician.profile.vehicleType ?? '',
        teamSize: action.technician.profile.teamSize == null ? '' : String(action.technician.profile.teamSize),
        // The identifier as stored, not as derived: an admin who only fixes a phone number must
        // not rename the login. The derivation takes over as soon as a name is actually typed.
        username: action.technician.username,
        lang: resolveLang(action.technician.lang),
        isActive: action.technician.isActive,
        countryCode: action.technician.profile.countryCode ?? '',
        geoAreaIds: action.technician.geoAreaIds,
        secondaryGeoAreaIds: action.technician.secondaryGeoAreaIds,
        specialtyIds: action.technician.specialtyIds,
        expertiseIds: action.technician.expertiseIds,
        addresses: draftsOf(action.technician),
        referenceKind: action.technician.addresses.find((address) => address.isDefault)?.kind ?? null,
      };
    case 'patch': {
      const touchesName = 'firstName' in action.patch || 'lastName' in action.patch;
      const next = { ...state, ...action.patch };
      // Recomputed on every name keystroke; never emptied, because `username` is a required `name`.
      if (!touchesName) return next;
      return { ...next, username: derivedUsername(next.firstName, next.lastName) || state.username };
    }
    default:
      return state;
  }
};

// Which screen lists the account that already holds the email: technicians are not in Users.
const EMAIL_HOLDER_SCREEN: Record<string, 'users' | 'technicians'> = {
  TECHNICIAN_EMAIL_TAKEN: 'users',
  TECHNICIAN_EMAIL_TAKEN_INACTIVE: 'users',
  TECHNICIAN_EMAIL_TAKEN_BY_TECHNICIAN: 'technicians',
};

const AdminTechnician = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  /**
   * Now that the directory is the way in, the record needs a way out. `navigate(-1)` keeps the
   * list's filters, which live in its query string — but a deep link has no in-app history to go
   * back to, and react-router marks that first entry `key: 'default'`.
   */
  const backToList = () =>
    location.key === 'default' ? navigate('/admin/technicians') : navigate(-1);
  const { currentUser } = useContext(AppContext);

  // A single `:id` route serves both screens, so `new` arrives here as the id. Anything else that
  // is not a plain number stays null rather than becoming `NaN`, which would be fetched as
  // `/admin/technicians/NaN` and answered 400.
  const creating = id === 'new';
  const technicianId = creating || !/^\d+$/.test(id ?? '') ? null : Number(id);
  const { data: technician, isLoading, error } = useTechnician(technicianId);
  const createTechnician = useCreateTechnician();
  const updateTechnician = useUpdateTechnician();
  const saving = createTechnician.isPending || updateTechnician.isPending;
  const [form, dispatch] = useReducer(reducer, EMPTY);
  // The email's refusal points at another screen; shown only while the field still holds that email.
  const refusal = creating ? createTechnician.error : updateTechnician.error;
  const refusedEmail = creating ? createTechnician.variables?.email : updateTechnician.variables?.data.email;
  const emailHolder =
    refusal instanceof ApiError && refusedEmail === form.email ? EMAIL_HOLDER_SCREEN[refusal.errorCode ?? ''] ?? null : null;

  /**
   * Creation only, and deliberately outside the record's reducer: the rights live on `users`, not
   * on the business record, and an existing one has the card write them on `/users/:id` instead.
   */
  const [creationRights, setCreationRights] = useState<UserRights>(TECHNICIAN_DEFAULT_RIGHTS);
  const [confirmingDeactivation, setConfirmingDeactivation] = useState(false);
  const [exclusionDialog, setExclusionDialog] = useState<ExclusionDialogMode | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [lastAuditEvent, setLastAuditEvent] = useState<AdminAuditEvent | null>(null);

  // CA10 — no button, no line, no request without the permission: `HistoryDrawer` owns the audit
  // hook itself and only ever mounts under this check.
  const hasAuditPermission = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_AUDIT_VIEW);
  // Nothing is flagged before the first submit — shouting at a field nobody has reached yet is how
  // a form loses a reader. From then on the map recomputes, so fixing a field clears its message.
  const [submitted, setSubmitted] = useState(false);

  /**
   * The mode is local state, not `?edit=`: an URL parameter would make "Annuler" a history entry,
   * so a browser Back would reopen the draft that was just abandoned. The list's pencil promises
   * an edit, and says so through a location state — which pollutes neither history nor a link.
   */
  const editIntent = (location.state as { edit?: boolean } | null)?.edit === true;
  const [editing, setEditing] = useState(creating || editIntent);
  const [leaving, setLeaving] = useState<'read' | 'list' | null>(null);
  const loadedId = useRef<number | null>(null);
  const langPicked = useRef(false);

  useEffect(() => {
    // Once per record, not on every refetch: a background refetch must not wipe what is being typed.
    if (technician && loadedId.current !== technician.id) {
      loadedId.current = technician.id;
      dispatch({ type: 'load', technician });
    }
  }, [technician]);

  useEffect(() => {
    // The pencil's intent is spent on arrival: history state survives a reload, which must come back in read mode.
    if (editIntent) navigate(location.pathname, { replace: true, state: null });
  }, [editIntent, location.pathname, navigate]);

  // A viewer without the manage permission reads the record; it renders disabled fields rather
  // than buttons that would 403. `canEdit` is the server's own verdict on this row.
  const canManage =
    hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_TECHNICIANS_MANAGE) &&
    !(!creating && technician?.canEdit === false);
  // Not a mode of its own: a read-only account is simply never allowed to leave read mode.
  const readOnly = !canManage || !editing;

  useEffect(() => {
    // The permission can go while the form is open: collapse the pair rather than offer a save
    // that the disabled fields have already made pointless.
    if (!canManage && editing) setEditing(false);
  }, [canManage, editing]);

  /**
   * The country pre-fills the language, and only while nobody has picked one: the pre-fill is a
   * proposal, so changing country afterwards must not undo a deliberate choice.
   */
  const patch = (values: Partial<FormState>) => {
    if (values.lang !== undefined) langPicked.current = true;
    const proposed = values.countryCode ? COUNTRY_LANG[values.countryCode] : undefined;
    const withLang = proposed && !langPicked.current ? { ...values, lang: proposed } : values;
    dispatch({ type: 'patch', patch: withLang });
  };

  // The PUT body is the whole record, so a payload is built from a state rather than patched.
  const toPayload = (state: FormState) => ({
    username: state.username,
    email: state.email,
    firstName: state.firstName || null,
    lastName: state.lastName || null,
    company: state.company,
    phone: state.phone,
    // F30 — the five profile fields ride the upsert; the ops note and the exclusion never do.
    contractType: state.contractType || null,
    profileKind: state.profileKind || null,
    experienceLevel: state.experienceLevel || null,
    vehicleType: state.vehicleType || null,
    teamSize: parseTeamSize(state.teamSize),
    countryCode: state.countryCode || null,
    lang: state.lang,
    geoAreaIds: state.geoAreaIds,
    // Always both, never "absent = no change" (F18): the backend never has to guess a side to keep.
    secondaryGeoAreaIds: nonOverlappingSecondary(state.geoAreaIds, state.secondaryGeoAreaIds),
    specialtyIds: state.specialtyIds,
    expertiseIds: state.expertiseIds,
    addresses: addressesPayload(state),
  });

  const payload = toPayload(form);

  const submit = () => {
    if (saving) return;
    if (creating) {
      createTechnician.mutate(
        { ...payload, password: form.password, ...creationRights },
        {
          onSuccess: ({ technician: created, jira }) => {
            // One toast, not two: the account exists, and the Jira half is a caveat on the same
            // sentence rather than a second event.
            const notice = provisioningNotice(t, created.username, jira);
            toast[notice.tone](notice.message);
            setEditing(false); // the same component serves the new id: it would stay in edit
            setSubmitted(false);
            navigate(`/admin/technicians/${created.id}`, { replace: true });
          },
          onError: (mutationError) => toast.error(saveErrorMessage(t, mutationError)),
        }
      );
      return;
    }
    // An empty box sends no `password` at all, so the stored one is left alone.
    const { password } = form;
    updateTechnician.mutate(
      { id: technicianId as number, data: { ...payload, isActive: form.isActive, ...(password && { password }) } },
      {
        onSuccess: () => {
          patch({ password: '' });
          // Re-arm the load guard: the id has not changed, so without this the refetch is
          // ignored and read mode keeps painting the pre-save form instead of the server's.
          loadedId.current = null;
          setEditing(false);
          setSubmitted(false);
          toast.success(t('Record saved'));
          if (password) toast.success(t('Password changed'));
        },
        onError: (mutationError) => {
          // The switch states the server's truth, so a refused save puts it back rather than
          // leaving the screen claiming a deactivation that never happened. Typed text is kept.
          if (technician) patch({ isActive: technician.isActive });
          toast.error(saveErrorMessage(t, mutationError));
        },
      }
    );
  };

  // Recomputed on every render once a save was attempted, so a corrected field drops its message.
  const allErrors = (): FormErrors => ({ ...formErrors(form, creating), ...addressErrors(form) });
  const errors: FormErrors = submitted ? allErrors() : {};

  /**
   * The status is a form field now, so the switch no longer sends anything on its own — but losing
   * the deactivation confirmation with the button would have been a silent regression: it moves
   * onto Save, and fires only on the active → inactive step.
   *
   * Nothing leaves before the rules pass, and the first offending control takes focus: six cards
   * deep, a message the page never scrolls to is a message nobody reads.
   */
  const save = (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    setSubmitted(true);
    const found = allErrors();
    const firstInvalid = FIELD_ORDER.filter((field) => found[field])[0];
    if (firstInvalid) {
      document.getElementById(controlId(firstInvalid))?.focus();
      return;
    }
    if (!creating && technician?.isActive && !form.isActive) return setConfirmingDeactivation(true);
    return submit();
  };

  // The URL only carries the id; the crumb takes the name — or nothing at all while it loads,
  // which leaves the raw segment in place rather than flashing an empty crumb.
  const fullName = [technician?.profile.firstName, technician?.profile.lastName].filter(Boolean).join(' ');
  usePageCrumb(creating ? t('New technician') : fullName || technician?.username || null);

  const stored = technician ? reducer(EMPTY, { type: 'load', technician }) : EMPTY;
  const dirty = JSON.stringify(form) !== JSON.stringify(stored);

  const toRead = () => {
    dispatch({ type: 'load', technician: technician as Technician }); // discard: back to what is stored
    setEditing(false);
    setSubmitted(false);
  };

  // Leaving an edit with unsaved input confirms first. The browser's own Back is deliberately not
  // intercepted: a react-router blocker for one screen costs more than it protects, and a refresh
  // comes back in read mode anyway.
  const exit = (target: 'read' | 'list') => {
    if (editing && dirty) return setLeaving(target);
    return target === 'list' ? backToList() : toRead();
  };

  if (!creating && isLoading) {
    return (
      <div className="container mx-auto space-y-4 p-6">
        {[0, 1, 2, 3].map((row) => (
          <Skeleton key={row} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  if (!creating && (error || !technician)) {
    return (
      <div className="container mx-auto space-y-4 p-6">
        <p className="text-sm text-red-600">
          {error ? apiErrorMessage(t, error) : t('This technician does not exist')}
        </p>
        <Button variant="outline" onClick={() => navigate('/admin/technicians')}>
          {t('Back to the technicians')}
        </Button>
      </div>
    );
  }

  // The design draws this pair twice: header and footer. Six cards deep, going back up to find
  // "Enregistrer" is the flaw it corrected itself. The header's pair sits outside the <form>, so
  // both submit through the `form` attribute — one handler, and Entrée saves from any field.
  const editActions = (
    <Fragment key="edit">
      <Button type="button" variant="outline" onClick={() => exit(creating ? 'list' : 'read')} disabled={saving}>
        {t('Cancel')}
      </Button>
      <Button type="submit" form={FORM_ID} disabled={saving}>
        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
        {creating ? t('Create the technician') : t('Save the record')}
      </Button>
    </Fragment>
  );

  return (
    <div className="container mx-auto space-y-4 p-6">
      <ReferentialPageHeader
        title={creating ? t('New technician') : fullName || form.username || t('Technician')}
        subtitle={t('Identity, scope, skills and activity of a field technician')}
        leadingAction={
          <button
            type="button"
            onClick={() => exit('list')}
            aria-label={t('Back')}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] border border-zinc-200 bg-white text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-50"
          >
            <ArrowLeft className="h-[18px] w-[18px]" aria-hidden="true" />
          </button>
        }
        titleBadges={
          <>
            <span className="inline-flex items-center gap-2 text-sm font-medium text-zinc-600 dark:text-zinc-300">
              <span
                className={cn('h-2 w-2 rounded-full', form.isActive ? 'bg-emerald-500' : 'bg-zinc-400')}
                aria-hidden="true"
              />
              {form.isActive ? t('Active') : t('Inactive')}
            </span>
            {hasUnlocatedAddress(technician?.addresses ?? []) && <UnlocatedBadge label={t('Address not located')} />}
            {editing && (
              <span className="inline-flex h-[23px] items-center rounded-full bg-amber-50 px-2 text-xs font-medium text-amber-700 dark:bg-amber-950 dark:text-amber-400">
                {creating ? t('New record') : t('Edit mode')}
              </span>
            )}
          </>
        }
        secondaryAction={
          <Fragment>
            {/* Independent of edit mode (design §5.1): a read action, never in the way of a save.
                The panel itself portals to <body>, so this is also where it mounts. */}
            {hasAuditPermission && !creating && technician && (
              <HistoryDrawer
                entityType="technician,user"
                entityId={String(technician.id)}
                title={fullName || technician.username}
                open={historyOpen}
                onOpenChange={(next) => {
                  setHistoryOpen(next);
                  // The Radix trap is released by now; rAF lets the replacing control mount (CA16).
                  if (!next) requestAnimationFrame(() => document.getElementById(HISTORY_BUTTON_ID)?.focus());
                }}
                onLatestEvent={setLastAuditEvent}
              />
            )}
            {editing ? (
              editActions
            ) : canManage ? (
              <Fragment key="read">
                {!technician?.exclusion && (
                  <Button id={EXCLUSION_ACTION_ID} type="button" variant="outline" onClick={() => setExclusionDialog('exclude')}>
                    <Ban className="mr-1.5 h-4 w-4" aria-hidden="true" />
                    {t('Ban')}
                  </Button>
                )}
                <Button type="button" onClick={() => setEditing(true)}>
                  <Pencil className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  {t('Edit')}
                </Button>
              </Fragment>
            ) : null}
          </Fragment>
        }
      />

      {/* CA18 — nothing while the journal loads or errors; the line names the most recent event. */}
      {hasAuditPermission && !creating && technician && lastAuditEvent && (
        <p className="-mt-3 text-[12.5px] text-muted-foreground/70">{lastModifiedText(t, lastAuditEvent)}</p>
      )}

      {/* F29 — icon and words, never the colour alone (CA22). */}
      {technician?.exclusion && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm font-medium text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200">
          <span className="flex items-center gap-2">
            <Ban className="h-4 w-4 shrink-0" aria-hidden="true" />
            {technician.exclusion.excludedBy
              ? t('Banned on {{date}} by {{name}} · {{reason}}', {
                  date: formatDate(technician.exclusion.excludedAt),
                  name: technician.exclusion.excludedBy.name,
                  reason: exclusionReasonLabel(t, technician.exclusion.reason),
                })
              : t('Banned on {{date}} · {{reason}}', {
                  date: formatDate(technician.exclusion.excludedAt),
                  reason: exclusionReasonLabel(t, technician.exclusion.reason),
                })}
          </span>
          {canManage && (
            <span className="flex shrink-0 gap-2">
              <Button
                id={EXCLUSION_ACTION_ID}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setExclusionDialog('edit')}
              >
                {t('Change the reason')}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setExclusionDialog('reinstate')}>
                {t('Reinstate')}
              </Button>
            </span>
          )}
        </div>
      )}

      {/* A real form at last, so Entrée saves and the required/type="email" attributes describe a
          control that is genuinely part of one. `noValidate`: the browser's own bubble is one
          message at a time, in the browser's language, and it cannot say "72 octets". */}
      <form id={FORM_ID} noValidate onSubmit={save} className="space-y-4">
        <GeneralSection
          values={form}
          onChange={patch}
          readOnly={readOnly}
          creating={creating}
          isActive={form.isActive}
          onToggleActive={(isActive) => patch({ isActive })}
          externalId={technician?.externalId ?? null}
          storedUsername={technician?.username ?? null}
          errors={errors}
          emailHolder={emailHolder}
        />

        <ScopeSection
          countryCode={form.countryCode}
          geoAreaIds={form.geoAreaIds}
          secondaryGeoAreaIds={form.secondaryGeoAreaIds}
          onChange={patch}
          readOnly={readOnly}
          creating={creating}
          errors={errors}
        />

        <SkillsSection
          countryCode={form.countryCode}
          specialtyIds={form.specialtyIds}
          expertiseIds={form.expertiseIds}
          onChange={patch}
          readOnly={readOnly}
          creating={creating}
          errors={errors}
        />

        {/* Inside the <form>, unlike its mock-up position under the absences: one save (AC1). */}
        <LogisticsSection
          addresses={form.addresses}
          referenceKind={form.referenceKind}
          onChange={patch}
          readOnly={readOnly}
          countryCode={form.countryCode}
          stored={technician?.addresses ?? []}
          errors={errors}
        />
      </form>

      {/* Outside the <form>: on an existing record the rights are a partial PUT on `/users/:id`
          with its own permission and its own save. On a creation the same card edits the draft
          the create call carries — the pickers the users form used to offer. */}
      {creating ? (
        <RightsSection
          userId={null}
          value={creationRights}
          onChange={(values) => setCreationRights((current) => ({ ...current, ...values }))}
        />
      ) : (
        technicianId !== null && <RightsSection userId={technicianId} />
      )}

      {/* Read-only, without exception: the technician is the sole author of his absences (D4).
          Absent while creating — a record that does not exist yet cannot be said to be available,
          and there is no id to read periods for. */}
      {!creating && technician && (
        <UnavailabilitySection
          periods={technician.unavailabilities}
          availability={technician.availability}
        />
      )}

      {!creating && technician && (
        <OpsNoteSection technicianId={technician.id} opsNote={technician.opsNote ?? null} canManage={canManage} />
      )}

      {!creating && technician && <ActivitySection activity={technician.activity} />}

      <RatingsSection avg={technician?.ratings.avg ?? null} count={technician?.ratings.count ?? 0} />

      {/* No delete and no status button, in either state: a technician is deactivated (B-04)
          through the switch above, and the pair the design duplicates is all that is left. */}
      {!readOnly && (
        <div className="flex flex-wrap items-center justify-end gap-2">{editActions}</div>
      )}

      {technician && (
        <ExclusionDialog
          mode={exclusionDialog}
          technicianId={technician.id}
          name={fullName || technician.username}
          currentReason={technician.exclusion?.reason ?? null}
          onClose={() => setExclusionDialog(null)}
          // The Radix trap is released by now; rAF lets the replacing control mount.
          onDone={() => requestAnimationFrame(() => document.getElementById(EXCLUSION_ACTION_ID)?.focus())}
        />
      )}

      {leaving && (
        <ConfirmDeleteDialog
          open
          tone="warning"
          title={t('Discard your changes?')}
          description={t('The fields you edited are not saved yet. Leaving this mode restores the record as it is stored.')}
          confirmLabel={t('Discard')}
          onClose={() => setLeaving(null)}
          onConfirm={() => {
            const target = leaving;
            setLeaving(null);
            return target === 'list' ? backToList() : toRead();
          }}
        />
      )}

      {confirmingDeactivation && (
        <ConfirmDeleteDialog
          open
          tone="warning"
          title={t('Deactivate {{name}}?', { name: form.username })}
          description={t('Once deactivated, they can no longer sign in to the mobile app, nor receive interventions. Their record and their history are kept, and they can be reactivated at any time.')}
          confirmLabel={t('Deactivate and save')}
          loading={saving}
          onClose={() => {
            // The dialog only opens on a live account, so dismissing it puts the switch back:
            // nothing was sent, and a screen reading "Inactif" would be claiming otherwise.
            setConfirmingDeactivation(false);
            patch({ isActive: true });
          }}
          onConfirm={() => {
            setConfirmingDeactivation(false);
            return submit();
          }}
        />
      )}
    </div>
  );
};

export default AdminTechnician;
